import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PlatformWhatsAppConnections } from "@/components/pawkt/PlatformWhatsAppConnections";
import {
  ShieldCheck, LayoutGrid, Building2, ScrollText, LogOut, Search, ArrowLeft,
  Ban, RefreshCw, CreditCard, CalendarClock, X, Wallet, BarChart3, AlertTriangle,
  LifeBuoy, Download, Activity, DoorOpen, FileDown, Brain, Database, Menu, Phone,
} from "lucide-react";

/* ------------------------------------------------------------ data hook */
function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    setLoading(true);
    api.get(path).then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e)).finally(() => setLoading(false));
  }, [path]);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, deps.length ? deps : [path]);
  return { data, loading, error, reload };
}

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function ErrorState({ error, onRetry }) {
  return (
    <div data-testid="pa-error" className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center">
      <AlertTriangle className="mx-auto h-6 w-6 text-rose-400" />
      <div className="mt-2 font-medium text-rose-200">Couldn’t load this data</div>
      <div className="mt-1 text-sm text-slate-400">{apiErr(error)}</div>
      <button data-testid="pa-retry" onClick={onRetry} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800">
        <RefreshCw className="h-4 w-4" /> Retry
      </button>
    </div>
  );
}
const Loading = () => <div className="text-slate-400" data-testid="pa-loading">Loading…</div>;

/* charts */
function BarList({ items, labelKey, valueKey, money }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  if (!items.length) return <div className="text-sm text-slate-500">No data</div>;
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm"><span className="text-slate-300 truncate">{it[labelKey] || "—"}</span>
            <span className="font-medium text-slate-200">{money ? fmtINR(it[valueKey]) : it[valueKey]}</span></div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-slate-800"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${((it[valueKey] || 0) / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
function Spark({ series }) {
  if (!series || series.length < 2) return <div className="text-sm text-slate-500">Insufficient history</div>;
  const vals = series.map((s) => s.value);
  const max = Math.max(...vals), min = Math.min(...vals);
  const pts = series.map((s, i) => {
    const x = (i / (series.length - 1)) * 100;
    const y = 100 - (max === min ? 50 : ((s.value - min) / (max - min)) * 90 + 5);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-24 w-full">
      <polyline points={pts} fill="none" stroke="#10b981" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ------------------------------------------------------------ guard + shell */
export function PlatformProtected({ children }) {
  const [state, setState] = useState("checking");
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/platform/me").then(() => setState("ok")).catch(() => {
      setState("no"); navigate("/platform-admin/login", { replace: true });
    });
  }, [navigate]);
  if (state !== "ok") return <div className="min-h-screen bg-slate-950 grid place-items-center text-slate-400">Verifying access…</div>;
  return children;
}

const NAV = [
  { to: "/platform-admin", end: true, icon: LayoutGrid, label: "Command Center" },
  { to: "/platform-admin/overview", icon: Activity, label: "Overview" },
  { to: "/platform-admin/businesses", icon: Building2, label: "Businesses" },
  { to: "/platform-admin/revenue", icon: Wallet, label: "Revenue" },
  { to: "/platform-admin/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/platform-admin/exports", icon: Download, label: "Exports" },
  { to: "/platform-admin/system", icon: Activity, label: "System Health" },
  { to: "/platform-admin/audit", icon: ScrollText, label: "Audit Log" },
  { to: "/platform-admin/phone-numbers", icon: Phone, label: "AI Receptionist Numbers" },
  { to: "/platform-admin/shally-greetings", icon: Phone, label: "Shally Greetings" },
  { to: "/platform-admin/shally-pilot", icon: Phone, label: "Shally Outbound Pilot" },
  { to: "/platform-admin/business-outbound", icon: Phone, label: "Business Outbound Calling" },
  { to: "/platform-admin/business-onboarding", icon: Phone, label: "Business Number Onboarding" },
  { to: "/platform-admin/sales-leads", icon: Phone, label: "Platform Call Leads" },
  { to: "/platform-admin/receptionist-billing", icon: Phone, label: "AI Receptionist Billing" },
  { to: "/platform-admin/intelligence", icon: Brain, label: "Intelligence" },
  { to: "/platform-admin/explorer", icon: Database, label: "Data Explorer" },
  { to: "/platform-admin/data-export", icon: Download, label: "Data Export" },
];

function SupportBanner() {
  const [session, setSession] = useState(null);
  const poll = useCallback(() => {
    api.get("/platform/support/active").then((r) => setSession(r.data.active ? r.data.session : null)).catch(() => {});
  }, []);
  useEffect(() => {
    poll();
    const t = setInterval(poll, 20000);
    const onChange = () => poll();
    window.addEventListener("pa-support-changed", onChange);
    return () => { clearInterval(t); window.removeEventListener("pa-support-changed", onChange); };
  }, [poll]);
  if (!session) return null;
  const end = async () => {
    try { await api.post(`/platform/support/${session.id}/end`); toast.success("Support access ended"); setSession(null); window.dispatchEvent(new Event("pa-support-changed")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="pa-support-banner" className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-2 bg-amber-500 px-6 py-2 text-sm font-medium text-amber-950">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-bold uppercase tracking-wide">● Support Access Active</span>
        <span>Tenant: <b data-testid="pa-support-tenant">{session.tenant_name}</b></span>
        <span>Admin: {session.admin_email}</span>
        <span>Reason: {session.reason}</span>
        <span>Expires: {new Date(session.expires_at).toLocaleTimeString("en-IN")}</span>
      </div>
      <button data-testid="pa-support-exit" onClick={end} className="flex items-center gap-1.5 rounded-md bg-amber-950 px-3 py-1.5 text-amber-50 hover:bg-amber-900">
        <DoorOpen className="h-4 w-4" /> Exit Support Access
      </button>
    </div>
  );
}

export function PlatformShell() {
  const navigate = useNavigate();
  const { data: me } = useApi("/platform/me");
  const [open, setOpen] = useState(false);
  const logout = async () => { try { await api.post("/auth/logout"); } catch {} navigate("/platform-admin/login"); };
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 lg:flex overflow-x-hidden">
      <div className="lg:hidden sticky top-0 z-40 flex h-14 items-center justify-between border-b border-slate-800 bg-slate-950/90 px-4 backdrop-blur">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-400" /><span className="font-display font-extrabold tracking-tight">Pawkt Platform</span></div>
        <button data-testid="pa-mobile-menu" aria-label="Open menu" onClick={() => setOpen(true)} className="grid h-10 w-10 place-items-center rounded-lg border border-slate-800"><Menu className="h-5 w-5" /></button>
      </div>
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform border-r border-slate-800 bg-slate-900 transition-transform lg:static lg:z-auto lg:w-60 lg:translate-x-0 lg:bg-slate-900/60 flex flex-col ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
          <div className="h-8 w-8 rounded-lg bg-emerald-500/15 grid place-items-center"><ShieldCheck className="h-4 w-4 text-emerald-400" /></div>
          <div className="font-display font-extrabold tracking-tight">Pawkt Platform</div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" onClick={() => setOpen(false)}>
          <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Platform</div>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`pa-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-emerald-500/10 text-emerald-300" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-2 text-xs text-slate-400 truncate" data-testid="pa-admin-email">{me?.email}</div>
          <button onClick={logout} data-testid="pa-logout" className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 lg:h-screen lg:overflow-y-auto"><SupportBanner /><div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8"><Outlet /></div></main>
    </div>
  );
}

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={testid}>
    <div className="text-xs text-slate-400">{label}</div>
    <div className="mt-1 font-display text-2xl font-bold">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
  </div>
);
const Card = ({ title, children, right }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
    <div className="flex items-center justify-between"><h3 className="font-semibold">{title}</h3>{right}</div>
    <div className="mt-3">{children}</div>
  </div>
);
const kvItems = (obj) => Object.entries(obj || {}).map(([k, v]) => ({ k, v }));

/* ------------------------------------------------------------ overview */
export function PlatformOverview() {
  const { data, loading, error, reload } = useApi("/platform/overview");
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !data) return <Loading />;
  const b = data.businesses, t = data.totals;
  return (
    <div data-testid="pa-overview">
      <h1 className="font-display text-2xl font-bold">Platform Overview</h1>
      <p className="text-sm text-slate-400 mt-1">Real-time view of every Pawkt business.</p>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-300">Derived MRR</div>
            <div className="mt-1 font-display text-3xl font-extrabold" data-testid="pa-mrr">{fmtINR(data.derived_mrr.amount)}</div>
            <div className="text-xs text-slate-400 mt-1">{data.derived_mrr.note}</div>
          </div>
          <div className="text-right text-sm text-slate-400">Run-rate (ARR)<div className="font-semibold text-slate-200">{fmtINR(data.derived_mrr.arr_run_rate)}</div></div>
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Stat testid="pa-stat-total" label="Total businesses" value={b.total} sub={`${b.new_this_month} new this month`} />
        <Stat testid="pa-stat-active" label="Active" value={b.active} />
        <Stat testid="pa-stat-suspended" label="Suspended" value={b.suspended} />
        <Stat testid="pa-stat-free" label="Free" value={b.free} sub={`${b.paid} paid`} />
        <Stat label="Users" value={t.users} /><Stat label="Pet parents" value={t.customers} />
        <Stat label="Pets" value={t.pets} /><Stat label="Bookings" value={t.bookings} />
      </div>
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Plan distribution"><BarList items={kvItems(data.plan_distribution).map((x) => ({ k: x.k, v: x.v }))} labelKey="k" valueKey="v" /></Card>
        <Card title="Recent signups">
          <div className="space-y-2">{data.recent_signups.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-sm"><span className="text-slate-300 truncate">{s.name}</span><span className="text-slate-500">{s.plan} · {fmtDate(s.created_at)}</span></div>))}</div>
        </Card>
      </div>
      <PlatformWhatsAppConnections />
    </div>
  );
}

/* ------------------------------------------------------------ businesses (paginated) */
export function PlatformBusinesses() {
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const navigate = useNavigate();
  const path = `/platform/businesses?search=${encodeURIComponent(q)}&plan=${plan}&status=${status}&skip=${page * pageSize}&limit=${pageSize}`;
  const { data, loading, error, reload } = useApi(path, [path]);
  const applyFilter = (fn) => { fn(); setPage(0); };

  return (
    <div data-testid="pa-businesses">
      <h1 className="font-display text-2xl font-bold">Businesses</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <form onSubmit={(e) => { e.preventDefault(); applyFilter(() => setQ(search)); }} className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input data-testid="pa-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / owner / email"
            className="w-full rounded-lg border border-slate-800 bg-slate-900 pl-10 pr-3 py-2 text-sm outline-none focus:border-emerald-500" />
        </form>
        <select data-testid="pa-filter-plan" value={plan} onChange={(e) => applyFilter(() => setPlan(e.target.value))} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          <option value="">All plans</option><option value="free">Free</option><option value="starter">Starter</option><option value="growth">Growth</option><option value="multi-branch">Multi-Branch</option>
        </select>
        <select data-testid="pa-filter-status" value={status} onChange={(e) => applyFilter(() => setStatus(e.target.value))} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          <option value="">All status</option><option value="active">Active</option><option value="suspended">Suspended</option>
        </select>
      </div>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div> : (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500">
                <tr>{["Business", "Owner", "Plan", "Status", "Branches", "Users", "Customers", "Bookings", "Created"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
                {!loading && data?.items?.map((r) => (
                  <tr key={r.id} data-testid={`pa-biz-row-${r.id}`} onClick={() => navigate(`/platform-admin/businesses/${r.id}`)} className="cursor-pointer border-t border-slate-800 hover:bg-slate-800/40">
                    <td className="px-3 py-2.5 font-medium text-slate-100">{r.name}</td>
                    <td className="px-3 py-2.5 text-slate-400">{r.owner_email || "—"}</td>
                    <td className="px-3 py-2.5">{r.plan}</td>
                    <td className="px-3 py-2.5"><span className={r.status === "suspended" ? "text-rose-400" : "text-emerald-400"}>{r.status}</span></td>
                    <td className="px-3 py-2.5">{r.branch_count}</td><td className="px-3 py-2.5">{r.user_count}</td>
                    <td className="px-3 py-2.5">{r.customer_count}</td><td className="px-3 py-2.5">{r.booking_count}</td>
                    <td className="px-3 py-2.5 text-slate-500">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
                {!loading && data && !data.items.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">No businesses found</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm text-slate-400">
            <span data-testid="pa-page-info">{data ? `${data.total} total · page ${page + 1} of ${Math.max(1, Math.ceil(data.total / pageSize))}` : ""}</span>
            <div className="flex gap-2">
              <button data-testid="pa-page-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded-lg border border-slate-700 px-3 py-1.5 disabled:opacity-40">Prev</button>
              <button data-testid="pa-page-next" disabled={!data || (page + 1) * pageSize >= data.total} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-700 px-3 py-1.5 disabled:opacity-40">Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ action modal */
function ActionModal({ open, onClose, action, tenantId, onDone }) {
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [plan, setPlan] = useState("growth");
  const [days, setDays] = useState(14);
  const [mins, setMins] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setPassword(""); setPlan("growth"); setDays(14); setMins(30); } }, [open]);
  if (!open || !action) return null;
  const run = async () => {
    setBusy(true);
    try {
      const body = { reason, password };
      let url = "";
      if (action === "suspend") url = `/platform/businesses/${tenantId}/suspend`;
      if (action === "reactivate") url = `/platform/businesses/${tenantId}/reactivate`;
      if (action === "plan") { url = `/platform/businesses/${tenantId}/plan`; body.plan = plan; }
      if (action === "trial") { url = `/platform/businesses/${tenantId}/extend-trial`; body.days = Number(days); }
      if (action === "freetrial") url = `/saas/admin/tenant/${tenantId}/start-trial`;
      if (action === "internal" || action === "internal_revoke") { url = `/platform/businesses/${tenantId}/internal-business`; body.enabled = action === "internal"; }
      if (action === "support") { url = `/platform/support/start`; body.tenant_id = tenantId; body.duration_minutes = Number(mins); }
      await api.post(url, body);
      if (action === "support") window.dispatchEvent(new Event("pa-support-changed"));
      toast.success("Action completed"); onClose(); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const titles = { suspend: "Suspend business", reactivate: "Reactivate business", plan: "Change plan (administrative)", trial: "Extend trial", freetrial: "Start 30-day Free Plan trial (existing business)", support: "Start support access", internal: "Grant internal business (complimentary CRM)", internal_revoke: "Revoke internal business" };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="pa-action-modal">
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-5 text-slate-100">
        <div className="flex items-center justify-between"><h3 className="font-semibold">{titles[action]}</h3><button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button></div>
        {action === "plan" && (
          <select data-testid="pa-action-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="mt-4 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
            <option value="free">Free</option><option value="starter">Starter</option><option value="growth">Growth</option><option value="multi-branch">Multi-Branch</option>
          </select>
        )}
        {action === "trial" && <input data-testid="pa-action-days" type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} placeholder="Days" className="mt-4 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm" />}
        {action === "freetrial" && <p className="mt-4 text-xs text-amber-300" data-testid="pa-freetrial-note">Starts a 30-day trial from now. When it ends, every user in this business is restricted until a paid plan is verified. A trial can only be started once.</p>}
        {action === "internal" && <p className="mt-4 text-xs text-amber-300" data-testid="pa-internal-note">Unlimited standard CRM with no subscription required. Does not enable calling and does not waive AI, telecom or other third-party charges. Only works for allow-listed business IDs.</p>}
        {action === "support" && <input data-testid="pa-action-minutes" type="number" min={1} max={120} value={mins} onChange={(e) => setMins(e.target.value)} placeholder="Minutes (max 120)" className="mt-4 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm" />}
        <textarea data-testid="pa-action-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="mt-3 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm" rows={2} />
        <input data-testid="pa-action-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Re-enter your password" className="mt-3 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm" />
        <p className="mt-2 text-xs text-slate-500">This action is logged with your identity, reason and before/after state.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-700 px-4 py-2 text-sm">Cancel</button>
          <button data-testid="pa-action-confirm" disabled={busy || reason.length < 3 || !password} onClick={run} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">{busy ? "Working…" : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ tenant 360 */
const Field = ({ k, v }) => (<div><div className="text-xs text-slate-500">{k}</div><div className="text-sm font-medium">{v ?? "—"}</div></div>);

export function PlatformTenant() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi(`/platform/businesses/${tenantId}`, [tenantId]);
  const [action, setAction] = useState(null);
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !data) return <Loading />;
  const p = data.profile, s = data.subscription, f = data.financial;
  const suspended = p.status === "suspended";
  return (
    <div data-testid="pa-tenant">
      <button onClick={() => navigate("/platform-admin/businesses")} className="mb-3 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft className="h-4 w-4" /> Businesses</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">{p.name}</h1>
          <div className="text-sm text-slate-400">{s.plan_label} · <span className={suspended ? "text-rose-400" : "text-emerald-400"}>{p.status}</span> · Created {fmtDate(p.created_at)}</div></div>
        <div className="flex flex-wrap gap-2">
          {suspended
            ? <button data-testid="pa-reactivate" onClick={() => setAction("reactivate")} className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950"><RefreshCw className="h-4 w-4" /> Reactivate</button>
            : <button data-testid="pa-suspend" onClick={() => setAction("suspend")} className="flex items-center gap-1.5 rounded-lg border border-rose-500/40 text-rose-300 px-3 py-2 text-sm font-semibold hover:bg-rose-500/10"><Ban className="h-4 w-4" /> Suspend</button>}
          <button data-testid="pa-change-plan" onClick={() => setAction("plan")} className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm"><CreditCard className="h-4 w-4" /> Change plan</button>
          <button data-testid="pa-extend-trial" onClick={() => setAction("trial")} className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm"><CalendarClock className="h-4 w-4" /> Extend trial</button>
          <button data-testid="pa-start-free-trial" onClick={() => setAction("freetrial")} className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm"><CalendarClock className="h-4 w-4" /> Start 30-day trial</button>
          {s.internal_business?.enabled
            ? <button data-testid="pa-internal-revoke" onClick={() => setAction("internal_revoke")} className="flex items-center gap-1.5 rounded-lg border border-rose-500/40 text-rose-300 px-3 py-2 text-sm"><CreditCard className="h-4 w-4" /> Revoke internal</button>
            : s.internal_business?.allow_listed && <button data-testid="pa-internal-grant" onClick={() => setAction("internal")} className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 px-3 py-2 text-sm"><CreditCard className="h-4 w-4" /> Internal business</button>}
          <button data-testid="pa-support-start" onClick={() => setAction("support")} className="flex items-center gap-1.5 rounded-lg border border-amber-500/40 text-amber-300 px-3 py-2 text-sm hover:bg-amber-500/10"><LifeBuoy className="h-4 w-4" /> Support access</button>
        </div>
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <Card title="Business"><div className="space-y-3"><Field k="Owner" v={p.owner.name} /><Field k="Email" v={p.owner.email} /><Field k="Phone" v={p.owner.phone} /><Field k="Types" v={(p.business_types || []).join(", ")} /><Field k="Timezone" v={p.timezone} /></div></Card>
        <Card title="Subscription"><div className="space-y-3"><Field k="Plan" v={`${s.plan_label} (${fmtINR(s.monthly_price)}/mo)`} /><Field k="Trial ends" v={fmtDate(s.trial_ends_at)} /><div data-testid="pa-internal-status"><Field k="Internal business" v={s.internal_business?.effective ? "Yes — complimentary CRM" : s.internal_business?.enabled ? "Flag set (not allow-listed)" : "No"} /></div><p className="text-xs text-slate-500">{s.note}</p></div></Card>
        <Card title="Financial"><div className="space-y-3"><Field k="GMV processed" v={fmtINR(f.gmv_processed)} /><Field k="Refunded" v={fmtINR(f.refunded)} /><Field k="Outstanding" v={fmtINR(f.outstanding)} /><Field k="MRR contribution" v={fmtINR(f.mrr_contribution)} /><p className="text-xs text-slate-500">{f.note}</p></div></Card>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Usage vs limits"><div className="grid grid-cols-2 gap-2 text-sm">{Object.entries(data.usage).map(([k, v]) => (
          <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="font-medium">{v}{data.limits[k] < 0 ? "" : ` / ${data.limits[k]}`}</span></div>))}</div></Card>
        <Card title={`Users (${data.users.length})`}><div className="space-y-1.5 text-sm">{data.users.map((u) => (
          <div key={u.id} className="flex items-center justify-between"><span className="text-slate-300">{u.name || u.email} <span className="text-slate-500">· {u.role}</span></span><span className={u.active ? "text-emerald-400" : "text-rose-400"}>{u.active ? "active" : "disabled"}</span></div>))}</div></Card>
      </div>
      <ActionModal open={!!action} action={action} tenantId={tenantId} onClose={() => setAction(null)} onDone={reload} />
    </div>
  );
}

/* ------------------------------------------------------------ revenue */
const RANGES = { "": "All time", "7d": "Last 7 days", "30d": "Last 30 days", "month": "This month", "lastmonth": "Last month", "year": "This year" };
function rangeToDates(r) {
  const now = new Date(); const iso = (d) => d.toISOString();
  if (r === "7d") return [iso(new Date(now - 7 * 864e5)), ""];
  if (r === "30d") return [iso(new Date(now - 30 * 864e5)), ""];
  if (r === "month") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), ""];
  if (r === "lastmonth") return [iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), iso(new Date(now.getFullYear(), now.getMonth(), 1))];
  if (r === "year") return [iso(new Date(now.getFullYear(), 0, 1)), ""];
  return ["", ""];
}

export function PlatformRevenue() {
  const [range, setRange] = useState("");
  const [df, dt] = rangeToDates(range);
  const path = `/platform/revenue?date_from=${df}&date_to=${dt}`;
  const { data, loading, error, reload } = useApi(path, [path]);
  return (
    <div data-testid="pa-revenue">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold">Revenue</h1><p className="text-sm text-slate-400 mt-1">Pawkt subscription revenue and tenant GMV are shown separately.</p></div>
        <select data-testid="pa-revenue-range" value={range} onChange={(e) => setRange(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          {Object.entries(RANGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div> : (loading || !data) ? <div className="mt-4"><Loading /></div> : (
        <>
          <div className="mt-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
            <div className="text-xs uppercase tracking-wide text-emerald-300">Pawkt platform revenue</div>
            <div className="mt-1 flex items-baseline gap-3"><span className="font-display text-3xl font-extrabold" data-testid="pa-rev-mrr">{fmtINR(data.platform_revenue.derived_mrr)}</span><span className="text-sm text-slate-400">/ mo · ARR {fmtINR(data.platform_revenue.arr_run_rate)}</span></div>
            <div className="text-xs text-slate-400 mt-1">{data.platform_revenue.note}</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-4">
              <Stat label="Paid tenants" value={data.platform_revenue.paid_active_tenants} />
              <Stat label="Free tenants" value={data.platform_revenue.free_tenants} />
              <Stat label="New paid (mo)" value={data.platform_revenue.new_paid_this_month} />
              <Stat label="Suspended paid" value={data.platform_revenue.suspended_paid} />
            </div>
            <div className="mt-4"><div className="text-sm font-medium mb-2">MRR by plan</div>
              <BarList items={kvItems(Object.fromEntries(Object.entries(data.platform_revenue.by_plan).map(([k, v]) => [k, v.mrr]))).map((x) => ({ k: x.k, v: x.v }))} labelKey="k" valueKey="v" money /></div>
          </div>
          <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="text-xs uppercase tracking-wide text-slate-400">Tenant business GMV</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <Stat testid="pa-rev-gmv" label="GMV processed" value={fmtINR(data.tenant_business_revenue.gmv)} />
              <Stat label="Refunds" value={fmtINR(data.tenant_business_revenue.refunds)} />
              <Stat label="Net GMV" value={fmtINR(data.tenant_business_revenue.net_gmv)} />
              <Stat label="Outstanding" value={fmtINR(data.tenant_business_revenue.outstanding)} />
            </div>
            <div className="text-xs text-slate-400 mt-3">{data.tenant_business_revenue.note}</div>
            <div className="mt-5 grid gap-5 lg:grid-cols-3">
              <Card title="Top businesses by GMV"><BarList items={data.tenant_business_revenue.by_business} labelKey="name" valueKey="gmv" money /></Card>
              <Card title="GMV by plan"><BarList items={kvItems(data.tenant_business_revenue.by_plan).map((x) => ({ k: x.k, v: x.v }))} labelKey="k" valueKey="v" money /></Card>
              <Card title="GMV by business type"><BarList items={kvItems(data.tenant_business_revenue.by_business_type).map((x) => ({ k: x.k, v: x.v }))} labelKey="k" valueKey="v" money /></Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ analytics */
export function PlatformAnalytics() {
  const [bt, setBt] = useState("");
  const [plan, setPlan] = useState("");
  const qs = `business_type=${bt}&plan=${plan}`;
  const ov = useApi(`/platform/analytics/overview?${qs}`, [qs]);
  const pets = useApi(`/platform/analytics/pets?${qs}`, [qs]);
  const svc = useApi(`/platform/analytics/services?${qs}`, [qs]);
  const tr = useApi(`/platform/analytics/trends?${qs}`, [qs]);
  const geo = useApi(`/platform/analytics/geography?${qs}`, [qs]);
  const anyErr = ov.error || pets.error || svc.error || tr.error || geo.error;

  return (
    <div data-testid="pa-analytics">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Cross-Platform Analytics</h1><p className="text-sm text-slate-400 mt-1">Aggregate trends across all Pawkt businesses.</p></div>
        <div className="flex gap-2">
          <select data-testid="pa-an-type" value={bt} onChange={(e) => setBt(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
            <option value="">All verticals</option><option value="grooming">Grooming</option><option value="vet_clinic">Vet</option><option value="boarding">Boarding</option><option value="daycare">Daycare</option><option value="training">Training</option><option value="pet_store">Pet store</option>
          </select>
          <select data-testid="pa-an-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
            <option value="">All plans</option><option value="free">Free</option><option value="starter">Starter</option><option value="growth">Growth</option><option value="multi-branch">Multi-Branch</option>
          </select>
        </div>
      </div>
      {anyErr && <div className="mt-4"><ErrorState error={anyErr} onRetry={() => { ov.reload(); pets.reload(); svc.reload(); tr.reload(); geo.reload(); }} /></div>}
      {ov.data && (
        <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Businesses" value={ov.data.total_businesses} /><Stat label="Active" value={ov.data.active_businesses} />
          <Stat label="Users" value={ov.data.totals.users} /><Stat label="Pet parents" value={ov.data.totals.customers} />
          <Stat label="Pets" value={ov.data.totals.pets} /><Stat label="Bookings" value={ov.data.totals.bookings} />
        </div>
      )}
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {ov.data && <Card title="Businesses by vertical"><BarList items={kvItems(ov.data.by_vertical).map((x) => ({ k: x.k, v: x.v }))} labelKey="k" valueKey="v" /></Card>}
        {ov.data && <Card title="Businesses by plan"><BarList items={kvItems(ov.data.by_plan).map((x) => ({ k: x.k, v: x.v }))} labelKey="k" valueKey="v" /></Card>}
        {pets.data && <Card title="Pet species"><BarList items={pets.data.species} labelKey="species" valueKey="count" /></Card>}
        {pets.data && <Card title="Top breeds"><BarList items={pets.data.top_breeds} labelKey="breed" valueKey="count" /></Card>}
        {svc.data && <Card title="Popular services (by bookings)"><BarList items={svc.data.popular_services} labelKey="service" valueKey="bookings" /></Card>}
        {svc.data && <Card title="Service categories"><BarList items={svc.data.categories} labelKey="category" valueKey="count" /></Card>}
        {tr.data && <Card title="Monthly bookings" right={tr.data.insufficient_history ? <span className="text-xs text-amber-400">Limited history</span> : null}><Spark series={tr.data.monthly_bookings} /></Card>}
        {tr.data && <Card title="Monthly GMV"><Spark series={tr.data.monthly_gmv} /></Card>}
        {tr.data && <Card title="Booking status"><BarList items={tr.data.booking_status} labelKey="status" valueKey="count" /></Card>}
        {geo.data && <Card title="Customers by city"><BarList items={geo.data.customers_by_city} labelKey="city" valueKey="count" /></Card>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ exports */
const DATASETS = ["businesses", "users", "customers", "pets", "bookings", "services", "revenue_summary", "analytics_summary"];
export function PlatformExports() {
  const [dataset, setDataset] = useState("businesses");
  const [fmt, setFmt] = useState("csv");
  const [tenantId, setTenantId] = useState("");
  const { data, loading, error, reload } = useApi("/platform/exports");
  const create = async () => {
    try {
      await api.post("/platform/exports", { dataset, fmt, tenant_id: tenantId || null });
      toast.success("Export requested"); setTimeout(reload, 800); setTimeout(reload, 2000);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const download = async (j) => {
    try {
      const r = await api.get(`/platform/exports/${j.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${j.dataset}.${j.fmt === "json" ? "json" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Downloaded"); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="pa-exports">
      <h1 className="font-display text-2xl font-bold">Data Exports</h1>
      <p className="text-sm text-slate-400 mt-1">Background exports. Sensitive fields (passwords, tokens, secrets) are never included. Links expire.</p>
      <div className="mt-4 flex flex-wrap items-end gap-2">
        <select data-testid="pa-export-dataset" value={dataset} onChange={(e) => setDataset(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          {DATASETS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="pa-export-fmt" value={fmt} onChange={(e) => setFmt(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm"><option value="csv">CSV</option><option value="json">JSON</option></select>
        <input data-testid="pa-export-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="Tenant ID (optional)" className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm" />
        <button data-testid="pa-export-create" onClick={create} className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400"><FileDown className="h-4 w-4" /> Create export</button>
      </div>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div> : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>{["Dataset", "Format", "Status", "Rows", "Requested by", "When", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
              {data?.items?.map((j) => (
                <tr key={j.id} data-testid={`pa-export-row-${j.id}`} className="border-t border-slate-800">
                  <td className="px-3 py-2.5 font-medium">{j.dataset}</td><td className="px-3 py-2.5">{j.fmt}</td>
                  <td className="px-3 py-2.5"><span className={j.status === "failed" ? "text-rose-400" : j.status === "ready" || j.status === "downloaded" ? "text-emerald-400" : "text-amber-400"}>{j.status}</span></td>
                  <td className="px-3 py-2.5">{j.row_count ?? "—"}</td><td className="px-3 py-2.5 text-slate-400">{j.requested_email}</td>
                  <td className="px-3 py-2.5 text-slate-500">{new Date(j.created_at).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5">{(j.status === "ready" || j.status === "downloaded") && <button data-testid={`pa-export-download-${j.id}`} onClick={() => download(j)} className="rounded-lg border border-slate-700 px-2.5 py-1 text-xs hover:bg-slate-800">Download</button>}</td>
                </tr>
              ))}
              {data && !data.items.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No exports yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ system health */
const Chip = ({ ok, warn, children }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${warn ? "bg-amber-500/15 text-amber-300" : ok ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{children}</span>
);
export function PlatformSystem() {
  const { data, loading, error, reload } = useApi("/platform/system");
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !data) return <Loading />;
  const pp = data.payment_provider;
  return (
    <div data-testid="pa-system">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold">System Health</h1><p className="text-sm text-slate-400 mt-1">Operational status only — no secrets are ever shown.</p></div>
        <button onClick={reload} data-testid="pa-system-refresh" className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <Card title="API & Database"><div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">API</span><Chip ok>{data.api.status}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Database</span><Chip ok={data.database.connected}>{data.database.status}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">DB latency</span><span className="font-medium">{data.database.latency_ms} ms</span></div>
        </div></Card>
        <Card title="Background jobs & outbox"><div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Scheduler</span><Chip ok={data.background_jobs.scheduler === "configured"}>{data.background_jobs.scheduler}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Outbox pending</span><span className="font-medium">{data.notifications_outbox.pending}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Outbox failed</span><Chip ok={!data.notifications_outbox.failed} warn={!!data.notifications_outbox.failed}>{data.notifications_outbox.failed}</Chip></div>
        </div></Card>
        <Card title="Payment provider"><div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Razorpay keys</span><Chip ok={pp.razorpay_keys === "configured"} warn={pp.razorpay_keys !== "configured"}>{pp.razorpay_keys}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Webhook secret</span><Chip ok={pp.razorpay_webhook === "configured"} warn={pp.razorpay_webhook !== "configured"}>{pp.razorpay_webhook}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Payment processing</span><Chip ok={pp.payment_processing === "enabled"} warn={pp.payment_processing !== "enabled"}>{pp.payment_processing}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Subscription billing</span><Chip warn>{pp.subscription_billing}</Chip></div>
          <p className="text-xs text-slate-500">{pp.note}</p>
        </div></Card>
        <Card title="Recent issues"><div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Failed webhooks</span><Chip ok={!data.recent_issues.failed_webhooks} warn={!!data.recent_issues.failed_webhooks}>{data.recent_issues.failed_webhooks}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Needs-review payments</span><Chip ok={!data.recent_issues.needs_review_payments} warn={!!data.recent_issues.needs_review_payments}>{data.recent_issues.needs_review_payments}</Chip></div>
          <div className="flex justify-between"><span className="text-slate-400">Media storage</span><Chip ok>{data.media_storage.status}</Chip></div>
        </div></Card>
      </div>
      {data.analytics_health && (
        <div className="mt-4" data-testid="pa-analytics-health">
          <Card title="Analytics / Intelligence Health">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-400">Overall</span>
              <Chip ok={data.analytics_health.overall_status === "HEALTHY"}
                warn={["STALE", "DEGRADED", "UNKNOWN"].includes(data.analytics_health.overall_status)}>
                {data.analytics_health.overall_status}
              </Chip>
            </div>
            {data.analytics_health.overall_status === "DISABLED" ? (
              <p className="text-xs text-slate-500">Pawkt Intelligence is switched off in this environment (setting INTEL_ENABLED=false), so its reports are not served. The Command Center is not affected.</p>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-slate-800">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>
                      {["Rollup", "Status", "Last computed", "Source max", "Version"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {(data.analytics_health.rollups || []).map((r) => (
                        <tr key={r.name} className="border-t border-slate-800" data-testid={`pa-rollup-${r.name}`}>
                          <td className="px-3 py-2">{r.label}</td>
                          <td className="px-3 py-2"><Chip ok={r.status === "FRESH"} warn={["STALE", "UNKNOWN", "NO_DATA"].includes(r.status)}>{r.status}</Chip></td>
                          <td className="px-3 py-2 text-slate-400">{r.last_computed_at ? new Date(r.last_computed_at).toLocaleString() : "—"}</td>
                          <td className="px-3 py-2 text-slate-400">{r.source_max_updated_at ? new Date(r.source_max_updated_at).toLocaleString() : "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{r.computation_version}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex gap-6 text-sm">
                  <span className="text-slate-400">Stale tenants: <span className="font-medium text-slate-200" data-testid="pa-stale-tenant-count">{data.analytics_health.stale_tenant_count}</span></span>
                  <span className="text-slate-400">Failed rollups: <span className="font-medium text-slate-200">{data.analytics_health.failed_rollup_count}</span></span>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
export function PlatformAudit() {
  const { data, loading, error, reload } = useApi("/platform/audit");
  return (
    <div data-testid="pa-audit">
      <h1 className="font-display text-2xl font-bold">Platform Audit Log</h1>
      <p className="text-sm text-slate-400 mt-1">Append-only record of every platform-admin action.</p>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div> : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>{["When", "Actor", "Action", "Tenant", "Reason"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
              {data?.items?.map((a) => (
                <tr key={a.id} className="border-t border-slate-800">
                  <td className="px-3 py-2.5 text-slate-500">{new Date(a.created_at).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 text-slate-400">{a.actor_email}</td>
                  <td className="px-3 py-2.5 font-medium text-emerald-300">{a.action}</td>
                  <td className="px-3 py-2.5 text-slate-400">{a.target_tenant_id || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-400">{a.reason || "—"}</td>
                </tr>
              ))}
              {data && !data.items.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No actions yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
